import { useEffect, useState } from 'react';
import { CalendarDays, FileText, HeartPulse, MessageSquareText, RefreshCw } from 'lucide-react';
import { getUserAssessment } from '../../../api/assessment.api';
import moment from 'moment-jalaali';

const show = (value) => (value === 0 || value ? value : 'ثبت نشده');
const dietLabels = { omnivore: 'همه‌چیزخوار', vegetarian: 'گیاه‌خوار', vegan: 'وگن', pescatarian: 'ماهی‌خوار' };
const avoidanceLabels = { 'red-meat': 'گوشت قرمز', poultry: 'مرغ و گوشت سفید', seafood: 'ماهی و غذاهای دریایی', dairy: 'لبنیات', eggs: 'تخم‌مرغ', gluten: 'گلوتن و گندم' };

export default function AssessmentTab({ userId }) {
  const [data, setData] = useState(null);
  const [status, setStatus] = useState('loading');
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let active = true;
    setStatus('loading');
    setData(null);
    getUserAssessment(userId).then((res) => {
      if (!active) return;
      const assessment = res.data?.data ?? res.data;
      setData(assessment);
      setStatus(assessment ? 'ready' : 'empty');
    }).catch((error) => {
      if (active) setStatus(error.response?.status === 404 ? 'empty' : 'error');
    });
    return () => { active = false; };
  }, [userId, attempt]);

  if (status === 'loading') return (
    <div role="status" aria-label="در حال دریافت اطلاعات اولیه" className="space-y-5 motion-safe:animate-pulse">
      <span className="sr-only">در حال دریافت اطلاعات اولیه…</span>
      <div className="h-8 w-48 rounded-lg bg-[#ece9e1]" />
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">{[0, 1, 2, 3].map((item) => <div key={item} className="h-24 rounded-2xl bg-[#f3f1eb]" />)}</div>
      <div className="h-48 rounded-2xl bg-[#f3f1eb]" />
    </div>
  );

  if (status === 'empty' || status === 'error') return (
    <div dir="rtl" role={status === 'error' ? 'alert' : 'status'} className="rounded-2xl bg-[#f7f5ef] px-6 py-12 text-center">
      <FileText size={28} aria-hidden="true" className="mx-auto mb-4 text-[#87918d]" />
      <h3 className="text-base font-black text-[#263b34]">{status === 'empty' ? 'فرم ارزیابی هنوز ثبت نشده است' : 'دریافت اطلاعات اولیه انجام نشد'}</h3>
      <p className="mt-2 text-sm text-[#71807a]">{status === 'empty' ? 'پس از تکمیل فرم توسط کاربر، پاسخ‌ها اینجا نمایش داده می‌شوند.' : 'دوباره برای دریافت پاسخ‌های کاربر تلاش کنید.'}</p>
      {status === 'error' && <button onClick={() => setAttempt((value) => value + 1)} className="admin-secondary mx-auto mt-5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#315a38]"><RefreshCw size={16} />تلاش دوباره</button>}
    </div>
  );

  const healthItems = [
    ['الگوی غذایی', dietLabels[data.dietaryStyle]],
    ['پرهیزهای انتخاب‌شده', (data.foodAvoidances || []).map((item) => avoidanceLabels[item]).filter(Boolean).join('، ')],
    ['بیماری یا وضعیت پزشکی خاص', data.medicalConditions || data.illness],
    ['داروها و مکمل‌های مصرفی', data.medications || data.medication],
    ['درد، آسیب یا محدودیت حرکتی', data.physicalLimitations || data.pain],
    ['معلولیت یا شرایط جسمی ویژه', data.disabilities],
    ['حساسیت یا عدم تحمل غذایی', data.foodAllergies],
    ['پرهیزها و ملاحظات غذایی', data.dietaryRestrictions],
    ['توضیحات تکمیلی سلامت', data.healthNotes],
  ].filter(([, value]) => Boolean(value));
  const notes = [
    ['نکته‌ای که مربی باید بداند', data.notes],
    ['توضیحات دیگر کاربر', data.comment],
  ].filter(([, value]) => typeof value === 'string' && value.trim());
  const basics = [
    ['قد', data.height ? `${data.height} سانتی‌متر` : null],
    ['سن', data.age ? `${data.age} سال` : null],
    ['جنسیت', ({ female: 'خانم', male: 'آقا', other: 'سایر' })[data.gender] || data.gender],
    ['تجربه ورزشی', data.trainingExperience],
  ];

  return (
    <div dir="rtl" className="space-y-7 text-[#263b34]">
      <header className="flex flex-wrap items-start justify-between gap-3 border-b border-[#e5e1d8] pb-5">
        <div><p className="admin-kicker">فرم ارزیابی کاربر</p><h3 className="mt-1 text-xl font-black">اطلاعات اولیه کاربر</h3><p className="mt-2 text-xs leading-6 text-[#71807a]">مشخصات، اهداف و نکاتی که کاربر برای مربی ثبت کرده است.</p></div>
        {data.createdAt && <span className="flex items-center gap-2 rounded-lg bg-[#f3f1eb] px-3 py-2 text-xs text-[#69746f]"><CalendarDays size={14} aria-hidden="true" />ثبت: <time dateTime={data.createdAt} className="tabular-nums">{moment(data.createdAt).format('jYYYY/jMM/jDD')}</time></span>}
      </header>

      <dl className="grid grid-cols-2 gap-3 lg:grid-cols-4">{basics.map(([label, value]) => <div key={label} className="min-w-0 rounded-2xl bg-[#f7f5ef] p-4"><dt className="text-xs text-[#71807a]">{label}</dt><dd className="mb-0 mt-2 whitespace-pre-wrap break-words text-sm font-bold leading-7 tabular-nums">{show(value)}</dd></div>)}</dl>

      <section aria-labelledby="assessment-notes" className="overflow-hidden rounded-2xl border border-[#dce5d9] bg-[#f2f6ef]">
        <div className="flex items-center gap-2 border-b border-[#dce5d9] px-5 py-4"><MessageSquareText size={18} aria-hidden="true" className="text-[#315a38]" /><h4 id="assessment-notes" className="text-sm font-black">یادداشت‌ها و شرایط زندگی کاربر</h4></div>
        <div className="space-y-5 p-5 sm:p-6">{notes.length ? notes.map(([label, value]) => <div key={label}><h5 className="mb-3 text-xs font-bold text-[#52654e]">{label}</h5><p className="mb-0 max-w-3xl whitespace-pre-wrap break-words text-sm leading-8 [overflow-wrap:anywhere]">{value}</p></div>) : <p className="mb-0 text-sm leading-7 text-[#71807a]">کاربر یادداشتی درباره شرایط کاری، محدودیت زمانی یا سایر نکات ثبت نکرده است.</p>}</div>
      </section>

      <section aria-labelledby="assessment-goals"><h4 id="assessment-goals" className="mb-4 text-sm font-black">اهداف و فعالیت</h4><dl className="grid gap-x-6 gap-y-4 sm:grid-cols-3">{[['هدف اصلی', data.mainGoal || data.reason], ['سطح فعالیت', data.activityLevel], ['تعداد روزهای تمرین در هفته', data.trainingDays]].map(([label, value]) => <div key={label} className="border-b border-[#e5e1d8] pb-4"><dt className="text-xs text-[#71807a]">{label}</dt><dd className="mb-0 mt-2 whitespace-pre-wrap break-words text-sm font-bold leading-7">{show(value)}</dd></div>)}</dl></section>

      <section aria-labelledby="assessment-health"><div className="mb-4 flex items-center gap-2"><HeartPulse size={18} aria-hidden="true" className="text-[#69746f]" /><h4 id="assessment-health" className="text-sm font-black">سلامت، محدودیت‌ها و تغذیه</h4></div>{healthItems.length ? <dl className="grid gap-x-8 gap-y-5 sm:grid-cols-2">{healthItems.map(([label, value]) => <div key={label} className="min-w-0 border-b border-[#e5e1d8] pb-4"><dt className="text-xs font-bold text-[#71807a]">{label}</dt><dd className="mb-0 mt-2 whitespace-pre-wrap break-words text-sm leading-8 [overflow-wrap:anywhere]">{value}</dd></div>)}</dl> : <p className="rounded-xl bg-[#f7f5ef] p-4 text-sm leading-7 text-[#71807a]">کاربر مورد خاصی اعلام نکرده است.</p>}</section>
    </div>
  );
}
